import { Controller } from "@hotwired/stimulus";
import { createGrid, GridApi, ColDef, ICellRendererParams, LicenseManager } from "ag-grid-enterprise";
import { ebTheme } from "../../../application/toolkit/ag_grid_shared/eb_theme";

interface VoterRow {
  id: number;
  eligible: boolean;
  voter: string;
  notice_statuses?: { key: string; label: string; status: string }[];
  destination?: string;
  status?: string;
  sent_at?: string;
  error?: string;
  channel_error?: string;
}

export default class extends Controller {
  static targets = ["notice", "send", "message", "grid", "jobsStatus", "jobsList", "jobsObserved", "pdfCompletion"];
  static values = { url: String, licenseKey: String };
  declare noticeTarget: HTMLSelectElement;
  declare sendTarget: HTMLButtonElement;
  declare messageTarget: HTMLElement;
  declare gridTarget: HTMLElement;
  declare jobsStatusTarget: HTMLElement;
  declare jobsListTarget: HTMLElement;
  declare jobsObservedTarget: HTMLElement;
  declare pdfCompletionTarget: HTMLElement;
  declare urlValue: string;
  declare licenseKeyValue: string;
  private api: GridApi<VoterRow> | null = null;
  private busy = false;
  private selectedIds = new Set<number>();
  private request: AbortController | null = null;

  connect(): void {
    if (this.licenseKeyValue) LicenseManager.setLicenseKey(this.licenseKeyValue);
    this.api = createGrid<VoterRow>(this.gridTarget, {
      theme: ebTheme,
      rowModelType: "infinite",
      cacheBlockSize: 100,
      maxBlocksInCache: 5,
      getRowId: ({ data }) => String(data.id),
      defaultColDef: { flex: 1, minWidth: 160, resizable: true, wrapHeaderText: true, autoHeaderHeight: true },
      enableBrowserTooltips: true,
      rowHeight: 52,
      selectionColumnDef: { width: 52, minWidth: 52, pinned: "left" },
      onSelectionChanged: () => this.updateSelection(),
      rowSelection: { mode: "multiRow", enableClickSelection: false, headerCheckbox: false, isRowSelectable: node => Boolean(node.data?.eligible) },
      columnDefs: this.columns(),
    });
    this.refresh();
  }

  disconnect(): void {
    this.request?.abort();
    this.api?.destroy();
    this.api = null;
  }

  changeNotice(): void {
    this.selectedIds = new Set(this.api?.getSelectedRows().map(row => row.id) || []);
    this.api?.deselectAll();
    this.api?.setGridOption("columnDefs", this.columns());
    this.updateSelection();
    this.messageTarget.textContent = this.noticeTarget.value && !this.sendable() ? "This notice is unavailable for sending. You can still view its status." : "";
    this.refresh();
  }

  refresh(): void {
    const noticeKey = this.noticeTarget.value;
    const selectedIds = this.selectedIds;
    this.selectedIds = new Set<number>();
    this.api?.setGridOption("datasource", {
      getRows: async params => {
        try {
          const url = new URL(this.urlValue, window.location.origin);
          url.searchParams.set("notice_key", noticeKey);
          url.searchParams.set("offset", String(params.startRow));
          const response = await fetch(url.toString(), { headers: { Accept: "application/json" } });
          if (!response.ok) throw new Error("Could not load notice status");
          const data = await response.json();
          if (params.startRow === 0) this.renderJobs(data.jobs);
          params.successCallback(data.rows, data.total);
          this.api?.forEachNode(node => {
            if (node.data && selectedIds.has(node.data.id) && node.data.eligible) node.setSelected(true);
          });
          this.updateSelection();
        } catch {
          params.failCallback();
          if (params.startRow === 0) this.jobsStatusTarget.textContent = "Job snapshot could not be refreshed; any displayed jobs are stale.";
          this.messageTarget.textContent = "Could not load notice status. Refresh to try again.";
        }
      }
    });
  }

  async sendSelected(): Promise<void> {
    const rows = this.api?.getSelectedRows() || [];
    if (rows.length === 0) {
      this.messageTarget.textContent = "Select at least one voter. Selection is limited to loaded rows (maximum 1,000 per request).";
      return;
    }
    await this.sendRows(rows.map(row => row.id));
  }

  private async sendRows(ids: number[], confirmed = false, noticeKey = this.noticeTarget.value, postageConfirmed = false): Promise<void> {
    if (this.busy || !this.sendable()) return;
    this.busy = true;
    this.noticeTarget.disabled = true;
    this.sendTarget.disabled = true;
    try {
      this.request = new AbortController();
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "" },
        body: JSON.stringify({ notice_key: noticeKey, invitation_ids: ids, confirmed, postage_confirmed: postageConfirmed }),
        signal: this.request.signal,
      });
      const data = await response.json();
      if (data.type === "postage_confirmation_required") {
        if (window.confirm(`This revalidates addresses and prepares fresh mail files before creating physical mail for ${ids.length} voter(s) and may incur new printing and postage charges, including duplicates. Continue?`)) {
          this.busy = false;
          await this.sendRows(ids, confirmed, noticeKey, true);
        }
        return;
      }
      if (data.type === "confirmation_required") {
        if (window.confirm(`${data.previous} selected voter(s) already have previous notice activity. Send this notice again to all ${ids.length} selected voter(s)?`)) {
          this.busy = false;
          await this.sendRows(ids, true, noticeKey, postageConfirmed);
        }
        return;
      }
      if (!response.ok) throw new Error(data.type || "Send failed");
      this.messageTarget.textContent = `${data.queued} queued; ${data.skipped} skipped (ineligible or already queued). Refresh for processing results.`;
      this.selectedIds.clear();
      this.api?.deselectAll();
      this.refresh();
    } catch {
      this.messageTarget.textContent = "Could not queue notices. Refresh status before trying again.";
    } finally {
      this.busy = false;
      this.noticeTarget.disabled = false;
      this.updateSelection();
    }
  }

  private renderJobs(jobs?: { available: boolean; rows: { label: string; status: string; count: number }[]; observed_at: string; last_pdf_generated_at?: string }): void {
    this.jobsListTarget.replaceChildren();
    this.jobsStatusTarget.textContent = !jobs?.available
      ? "Job snapshot unavailable — refresh to try again."
      : jobs.rows.length ? "Election jobs observed" : "No election jobs visible in this sample.";
    this.jobsObservedTarget.textContent = jobs?.observed_at ? `Observed: ${jobs.observed_at}` : "";
    for (const job of jobs?.rows || []) {
      const item = document.createElement("li");
      const label = document.createElement("span");
      label.textContent = job.label;
      const badge = document.createElement("span");
      const tone = job.status.startsWith("Failed") ? "error" : job.status.startsWith("Unknown") ? "neutral" : "pending";
      badge.className = `notice-management__status notice-management__status--${tone}`;
      badge.textContent = `${job.status} · ${job.count} observed`;
      item.append(label, badge);
      this.jobsListTarget.append(item);
    }
    this.pdfCompletionTarget.textContent = jobs?.last_pdf_generated_at
      ? `Last election-wide PDF completion stamp: ${jobs.last_pdf_generated_at}`
      : "No election-wide PDF completion stamp recorded.";
  }

  private updateSelection(): void {
    const count = this.api?.getSelectedRows().length || 0;
    this.sendTarget.textContent = count ? `Send selected (${count})` : "Send selected";
    this.sendTarget.disabled = !this.sendable() || this.busy || count === 0;
    this.sendTarget.title = !this.noticeTarget.value ? "Choose a notice to send" : !this.sendable() ? "This notice is unavailable for sending" : "Send to selected eligible voters";
  }

  private statusChip(status: string): HTMLElement {
    const chip = document.createElement("span");
    const styles: Record<string, [string, string, string]> = {
      "Preparing": ["pending", "◷", "Preparing"], "Queued": ["pending", "◷", "Queued"], "Sending": ["pending", "◷", "Sending"],
      "Submitted": ["pending", "↗", "Submitted"], "Sent": ["sent", "✓", "Sent"],
      "Delivered": ["sent", "✓", "Delivered"], "Read": ["sent", "✓", "Read"],
      "Failed": ["error", "!", "Failed"], "Error": ["error", "!", "Error"],
      "Uncertain": ["pending", "!", "Uncertain"],
      "Cancelled": ["neutral", "–", "Cancelled"], "Not sent": ["neutral", "○", "Not sent"],
      "Unknown (historical)": ["neutral", "?", "Unknown"],
      "Sent (historical channel activity)": ["neutral", "↗", "Past activity"],
    };
    const [tone, icon, label] = styles[status] || ["neutral", "?", status];
    chip.className = `notice-management__status notice-management__status--${tone}`;
    chip.textContent = `${icon} ${label}`;
    chip.title = status;
    return chip;
  }

  private summary(params: ICellRendererParams<VoterRow>): HTMLElement {
    const summary = document.createElement("div");
    summary.className = "notice-management__statuses";
    const grouped = new Map<string, string[]>();
    for (const notice of params.data?.notice_statuses || []) {
      grouped.set(notice.status, [...(grouped.get(notice.status) || []), notice.label]);
    }
    const priority = (status: string): number => ["Error", "Failed", "Uncertain"].includes(status) ? 0 : 1;
    summary.title = (params.data?.notice_statuses || []).map(notice => `${notice.label}: ${notice.status}`).join("\n");
    for (const [status, notices] of [...grouped].sort(([a], [b]) => priority(a) - priority(b))) {
      const chip = this.statusChip(status);
      chip.textContent += ` · ${notices.length}`;
      chip.title = `${status}\n${notices.join("\n")}`;
      summary.append(chip);
    }
    if (!grouped.size) summary.textContent = "No notices set up";
    return summary;
  }

  private sendable(): boolean {
    return this.noticeTarget.selectedOptions[0]?.dataset.sendable === "true";
  }

  private columns(): ColDef<VoterRow>[] {
    const columns: ColDef<VoterRow>[] = [{ field: "id", headerName: "Voter ID", flex: 0, width: 120, minWidth: 120 }, { field: "voter", headerName: "Voter" }];
    if (!this.noticeTarget.value) return [...columns, {
      headerName: "Election notice status", flex: 4, minWidth: 600,
      cellRenderer: (params: ICellRendererParams<VoterRow>) => this.summary(params),
    }];
    return [...columns,
      { field: "destination", headerName: "Destination", minWidth: 200, tooltipField: "destination" },
      { field: "status", headerName: "Status", minWidth: 180, cellRenderer: (params: ICellRendererParams<VoterRow>) => params.data ? this.statusChip(params.data.status || "Unknown") : "" },
      { headerName: "Sending", minWidth: 180, valueGetter: params => params.data ? (params.data.eligible ? "Eligible" : "Unavailable") : "", tooltipValueGetter: params => params.data?.eligible ? "Ready to send" : "Notice setup, voter status or destination prevents sending" },
      { field: "sent_at", headerName: "Last activity" },
      { field: "error", headerName: "Notice error", tooltipField: "error" },
      { field: "channel_error", headerName: "Channel error", headerTooltip: "May belong to another notice", minWidth: 180, tooltipField: "channel_error" },
      { headerName: "Action", minWidth: 170, cellRenderer: (params: ICellRendererParams<VoterRow>) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "toolkit-button-component toolkit-button-component--blue-stroke toolkit-button-component--tiny";
        button.textContent = "Send / Resend";
        button.title = params.data?.eligible && this.sendable() ? "Send this notice to this voter" : "This voter cannot receive this notice";
        button.disabled = !params.data?.eligible || !this.sendable();
        button.addEventListener("click", () => { if (params.data) void this.sendRows([params.data.id]); });
        return button;
      } }
    ];
  }
}
